import type { AgentExecutionDto } from '@terra/shared-contracts-agent-execution';
import { resolveAiDiscoveryStatusBarBucket } from './ai-discovery-run-status-bar.util';
import { getAiDiscoveryMetadata, getExecutionLabel } from './discovery.utils';
import type { McpDiscoveryRun } from './mcp-discovery-run.types';

const MCP_DISCOVERY_RUN_GAP_MS = 30 * 60 * 1000;

function toIsoTimestamp(value: Date | string): string {
  return typeof value === 'string' ? value : value.toISOString();
}

function toIsoTimestampOrNull(
  value: Date | string | null | undefined,
): string | null {
  if (value == null) {
    return null;
  }

  return toIsoTimestamp(value);
}

function resolveRunGroupKey({
  execution,
}: {
  execution: AgentExecutionDto;
}): string | null {
  const discoveryRunId = getAiDiscoveryMetadata(
    execution.metadata,
  )?.discoveryRunId;

  return typeof discoveryRunId === 'string' && discoveryRunId.length > 0
    ? discoveryRunId
    : null;
}

function clusterExecutionsWithoutRunId(
  executions: AgentExecutionDto[],
): AgentExecutionDto[][] {
  const sorted = sortDiscoveryExecutionsByTime(executions);

  if (sorted.length === 0) {
    return [];
  }

  const [firstExecution, ...remainingExecutions] = sorted;

  if (!firstExecution) {
    return [];
  }

  const groups: AgentExecutionDto[][] = [[firstExecution]];
  let previousExecution = firstExecution;

  for (const execution of remainingExecutions) {
    const gapMs =
      new Date(execution.createdAt).getTime() -
      new Date(previousExecution.createdAt).getTime();

    if (gapMs > MCP_DISCOVERY_RUN_GAP_MS) {
      groups.push([execution]);
    } else {
      const currentGroup = groups.at(-1);

      if (currentGroup) {
        currentGroup.push(execution);
      }
    }

    previousExecution = execution;
  }

  return groups;
}

function parseTimeMs(value: Date | string | null | undefined): number {
  if (value == null) {
    return 0;
  }

  const ms = typeof value === 'string' ? Date.parse(value) : value.getTime();

  return Number.isFinite(ms) ? ms : 0;
}

export function resolveMcpDiscoveryRunStatus({
  status,
  verifiedProbeCount,
  failedProbeCount = 0,
}: {
  status: AgentExecutionDto['status'];
  verifiedProbeCount: number;
  failedProbeCount?: number;
}): AgentExecutionDto['status'] {
  if (status === 'PENDING' || status === 'RUNNING' || status === 'ABORTED') {
    return status;
  }

  if (failedProbeCount > 0) {
    return 'FAILED';
  }

  if (verifiedProbeCount > 0) {
    return 'COMPLETED';
  }

  return status === 'COMPLETED' ? 'COMPLETED' : 'FAILED';
}

function resolveAggregateRunStatus(
  executions: AgentExecutionDto[],
): AgentExecutionDto['status'] {
  const statuses = executions.map((execution) => execution.status);

  if (statuses.some((status) => status === 'PENDING' || status === 'RUNNING')) {
    return 'RUNNING';
  }

  if (statuses.some((status) => status === 'FAILED' || status === 'BLOCKED')) {
    return 'FAILED';
  }

  if (statuses.some((status) => status === 'ABORTED')) {
    return 'ABORTED';
  }

  return 'COMPLETED';
}

export function sortMcpDiscoveryRunsByTime(
  runs: McpDiscoveryRun[],
): McpDiscoveryRun[] {
  return [...runs].sort((left, right) => {
    const startedDelta =
      parseTimeMs(right.startedAt) - parseTimeMs(left.startedAt);

    if (startedDelta !== 0) {
      return startedDelta;
    }

    return parseTimeMs(right.completedAt) - parseTimeMs(left.completedAt);
  });
}

export function sortDiscoveryExecutionsByTime(
  executions: AgentExecutionDto[],
): AgentExecutionDto[] {
  return [...executions].sort((left, right) => {
    const createdDelta =
      parseTimeMs(left.createdAt) - parseTimeMs(right.createdAt);

    if (createdDelta !== 0) {
      return createdDelta;
    }

    return getExecutionLabel({ execution: left }).localeCompare(
      getExecutionLabel({ execution: right }),
    );
  });
}

export function sortDiscoveryExecutionsNewestFirst(
  executions: AgentExecutionDto[],
): AgentExecutionDto[] {
  return [...executions].sort((left, right) => {
    const createdDelta =
      parseTimeMs(right.createdAt) - parseTimeMs(left.createdAt);

    if (createdDelta !== 0) {
      return createdDelta;
    }

    return getExecutionLabel({ execution: left }).localeCompare(
      getExecutionLabel({ execution: right }),
    );
  });
}

export function sortMcpDiscoveryToolExecutionsForDisplay(
  executions: AgentExecutionDto[],
): AgentExecutionDto[] {
  const rank = (execution: AgentExecutionDto): number => {
    const metadata = getAiDiscoveryMetadata(execution.metadata);
    const status = execution.status;

    if (metadata?.verdictStatus === 'VERIFIED' || status === 'COMPLETED') {
      return 0;
    }

    if (status === 'RUNNING' || status === 'PENDING') {
      return 1;
    }

    if (
      metadata?.verdictStatus === 'FAILED' ||
      status === 'FAILED' ||
      status === 'BLOCKED'
    ) {
      return 2;
    }

    return 3;
  };

  return [...executions].sort((left, right) => {
    const rankDelta = rank(left) - rank(right);

    if (rankDelta !== 0) {
      return rankDelta;
    }

    return getExecutionLabel({ execution: left }).localeCompare(
      getExecutionLabel({ execution: right }),
    );
  });
}

export function isMcpToolDiscoveryExecution(
  execution: AgentExecutionDto,
): boolean {
  const metadata = getAiDiscoveryMetadata(execution.metadata);

  if (metadata?.componentType === 'MCP_SERVER') {
    return false;
  }

  return (
    metadata?.componentType === 'MCP_TOOL' ||
    (metadata?.mcpToolName != null && metadata.mcpToolName.length > 0)
  );
}

export function buildMcpDiscoveryRun({
  id,
  executions,
}: {
  id: string;
  executions: AgentExecutionDto[];
}): McpDiscoveryRun {
  const sortedExecutions = sortDiscoveryExecutionsByTime(executions);
  const startedAt = toIsoTimestamp(
    sortedExecutions[0]?.createdAt ?? new Date().toISOString(),
  );
  const lastCreatedAtMs = sortedExecutions.reduce(
    (latestMs, execution) =>
      Math.max(latestMs, parseTimeMs(execution.createdAt)),
    parseTimeMs(startedAt),
  );
  const completedInRunWindow = sortedExecutions.filter((execution) => {
    if (execution.completedAt == null) {
      return false;
    }

    const completedAtMs = parseTimeMs(execution.completedAt);

    return (
      completedAtMs >= parseTimeMs(execution.createdAt) &&
      completedAtMs <= lastCreatedAtMs + MCP_DISCOVERY_RUN_GAP_MS
    );
  });
  const lastCompletedExecution =
    completedInRunWindow.length > 0
      ? completedInRunWindow.reduce((latest, execution) => {
          const executionCompletedAt = execution.completedAt;
          const latestCompletedAt = latest.completedAt;

          if (executionCompletedAt == null) {
            return latest;
          }

          if (latestCompletedAt == null) {
            return execution;
          }

          return parseTimeMs(executionCompletedAt) >
            parseTimeMs(latestCompletedAt)
            ? execution
            : latest;
        })
      : null;
  const lastCompletedAt = toIsoTimestampOrNull(
    lastCompletedExecution?.completedAt,
  );
  const toolExecutions = sortedExecutions.filter(isMcpToolDiscoveryExecution);
  const verifiedProbeCount = toolExecutions.filter((execution) => {
    const metadata = getAiDiscoveryMetadata(execution.metadata);

    return (
      metadata?.verdictStatus === 'VERIFIED' || execution.status === 'COMPLETED'
    );
  }).length;
  const failedProbeCount = toolExecutions.filter((execution) => {
    const metadata = getAiDiscoveryMetadata(execution.metadata);
    const status = execution.status;

    return (
      metadata?.verdictStatus === 'FAILED' ||
      status === 'FAILED' ||
      status === 'BLOCKED'
    );
  }).length;
  const aggregateStatus = resolveMcpDiscoveryRunStatus({
    status: resolveAggregateRunStatus(sortedExecutions),
    verifiedProbeCount,
    failedProbeCount,
  });
  const fallbackRepresentativeExecution = sortedExecutions.at(-1);
  const representativeExecution =
    sortedExecutions.find(
      (execution) =>
        execution.status === 'RUNNING' || execution.status === 'PENDING',
    ) ?? fallbackRepresentativeExecution;

  if (!representativeExecution) {
    throw new Error('Cannot build MCP discovery run without executions');
  }

  return {
    id,
    startedAt,
    completedAt: lastCompletedAt,
    durationMs:
      lastCompletedAt != null
        ? new Date(lastCompletedAt).getTime() - new Date(startedAt).getTime()
        : null,
    status: aggregateStatus,
    statusBarBucket: resolveAiDiscoveryStatusBarBucket({
      ...representativeExecution,
      status: aggregateStatus,
    }),
    probeCount: toolExecutions.length,
    verifiedProbeCount,
    failedProbeCount,
    representativeExecutionId: representativeExecution.id,
    executions: sortedExecutions,
  };
}

export function aggregateMcpDiscoveryRuns(
  executions: AgentExecutionDto[],
): McpDiscoveryRun[] {
  const groupedByRunId = new Map<string, AgentExecutionDto[]>();
  const ungroupedExecutions: AgentExecutionDto[] = [];

  for (const execution of executions) {
    const runId = resolveRunGroupKey({ execution });

    if (runId) {
      const existing = groupedByRunId.get(runId) ?? [];

      existing.push(execution);
      groupedByRunId.set(runId, existing);
      continue;
    }

    ungroupedExecutions.push(execution);
  }

  const runs = [
    ...[...groupedByRunId.entries()].map(([runId, groupedExecutions]) =>
      buildMcpDiscoveryRun({ id: runId, executions: groupedExecutions }),
    ),
    ...clusterExecutionsWithoutRunId(ungroupedExecutions).map(
      (groupedExecutions, index) =>
        buildMcpDiscoveryRun({
          id: groupedExecutions[0]?.id ?? `legacy-run-${index}`,
          executions: groupedExecutions,
        }),
    ),
  ];

  return sortMcpDiscoveryRunsByTime(runs);
}
